import type { FilterDef, FilterValue } from './filterTypes';

/**
 * Filter-value validation shared by the inputs (inline error message) and the
 * serializer (`appendFilterParams` skips invalid values so they never reach the
 * backend). Keep the two in sync via this single source of truth.
 */
export type FilterErrorKey =
  | 'invalidNumber'
  | 'negative'
  | 'belowMin'
  | 'aboveMax'
  | 'rangeOrder';

export interface FilterError {
  key: FilterErrorKey;
  /** i18n interpolation params (min / max), when relevant. */
  params?: Record<string, number>;
}

const toNum = (v: unknown): number | undefined => {
  if (typeof v === 'number') return Number.isNaN(v) ? undefined : v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
};

/**
 * Numeric bounds for a def. `min` defaults to 0 (business rule "no negatives")
 * unless the def's `inputProps.min` overrides it; `max` only applies when set
 * (e.g. the activity rate caps at 100).
 */
function numericBounds(def?: FilterDef): { min: number; max?: number } {
  const inputProps =
    def && 'inputProps' in def ? (def.inputProps ?? undefined) : undefined;
  const min = toNum(inputProps?.min);
  const max = toNum(inputProps?.max);
  return { min: min ?? 0, max };
}

/**
 * Returns the first validation error for a filter value, or `null` when valid.
 *
 * - numeric: rejects non-numbers (NaN), values below `min` (default 0 → no
 *   negatives) and above `max` when defined.
 * - dateRange (« entre »): rejects an incoherent range where `to` is strictly
 *   before `from` (ISO `YYYY-MM-DD` strings compare chronologically).
 *
 * Other kinds are always valid here.
 */
export function validateFilterValue(
  def: FilterDef | undefined,
  value: FilterValue | undefined,
): FilterError | null {
  if (!value) return null;

  if (value.kind === 'numeric') {
    const n = value.value;
    if (typeof n !== 'number' || Number.isNaN(n)) {
      return { key: 'invalidNumber' };
    }
    const { min, max } = numericBounds(def);
    if (n < min) {
      return min === 0 ? { key: 'negative' } : { key: 'belowMin', params: { min } };
    }
    if (max != null && n > max) {
      return { key: 'aboveMax', params: { max } };
    }
  }

  if (value.kind === 'dateRange') {
    if (value.from && value.to && value.to < value.from) {
      return { key: 'rangeOrder' };
    }
  }

  return null;
}
